/**
 * PageRegistryService - Otomatik sayfa keşif ve yönetim sistemi
 * SaaS menü yönetimi için tüm mevcut sayfaları tarar ve yönetir
 */

class PageRegistryService {
  // 🎯 CENTRAL PATH-MODULE MAPPING
  // Every path is linked to its required module ID here.
  static PAGE_MODULE_MAPPING = {
    // Food & Beverage (Modern paths)
    '/sectors/food-beverage/dashboard': ['core_access'],
    '/sectors/food-beverage/pos/dashboard': ['pos_basic'],
    '/sectors/food-beverage/pos/tables': ['pos_basic', 'table_management'],
    '/sectors/food-beverage/pos/table-service': ['pos_basic', 'table_management'],
    '/sectors/food-beverage/pos/quick-sales': ['pos_basic'],
    '/sectors/food-beverage/pos/phone-orders': ['pos_basic'],
    '/sectors/food-beverage/kitchen/kitchen-screen': ['kitchen_display'],
    '/sectors/food-beverage/operations/inventory-management': ['stock_basic'],
    '/sectors/food-beverage/finance/income-analysis': ['accounting'],
    '/sectors/food-beverage/finance/platform-analysis': ['accounting'],
    '/sectors/food-beverage/finance/expense-management': ['accounting'],
    '/sectors/food-beverage/hr/staff-management': ['hr_management'],
    '/sectors/food-beverage/hr/performance': ['hr_management'],
    '/sectors/food-beverage/menu/qr-menu-generator': ['qr_menu'],
    '/sectors/food-beverage/customers/customer-management': ['crm'],
    '/sectors/food-beverage/operations/order-center': ['pos_basic'],
    '/sectors/food-beverage/operations/order-tracking': ['pos_basic'],
    '/sectors/food-beverage/settings/table-management': ['table_management'],

    // Legacy Restaurant paths (Backward compatibility)
    '/sectors/restaurant/dashboard': ['core_access'],
    '/sectors/restaurant/pos/dashboard': ['pos_basic'],
    '/sectors/restaurant/pos/tables': ['pos_basic', 'table_management'],
    '/sectors/restaurant/pos/table-service': ['pos_basic', 'table_management'],
    '/sectors/restaurant/pos/quick-sales': ['pos_basic'],
    '/sectors/restaurant/pos/phone-orders': ['pos_basic'],
    '/sectors/restaurant/kitchen/kitchen-screen': ['kitchen_display'],
    '/sectors/restaurant/operations/inventory-management': ['stock_basic'],
    '/sectors/restaurant/finance/income-analysis': ['accounting'],
    '/sectors/restaurant/finance/expense-management': ['accounting'],
    '/sectors/restaurant/hr/staff-management': ['hr_management'],
    '/sectors/restaurant/hr/performance': ['hr_management'],
    '/sectors/restaurant/menu/qr-menu-generator': ['qr_menu'],
    '/sectors/restaurant/customers/customer-management': ['crm'],
    
    // Hospitality
    '/sectors/service/hospitality/dashboard': ['hospitality_core'],
    '/sectors/service/hospitality/reservations': ['reservation_management'],
    '/sectors/service/hospitality/finance': ['hotel_finance'],
    
    // Retail
    '/sectors/retail/dashboard': ['retail_core'],
    '/sectors/retail/inventory/products': ['stock_pro'],
    
    // Generic
    '/settings': ['basic_access'],
    '/subscription/unified': ['basic_access']
  };

  constructor() {
    this.pages = new Map();
    this.categories = new Map();
    this.initialized = false;
  }

  /**
   * Returns the required modules for a given path
   * @param {string} path The current route path
   * @returns {Array} List of module IDs
   */
  static getRequiredModules(path) {
    // Normalize path (remove query params)
    const cleanPath = path.split('?')[0];
    return this.PAGE_MODULE_MAPPING[cleanPath] || [];
  }

  /**
   * Tüm sayfa verilerini otomatik olarak tarar ve kaydetir
   */
  async initializePageRegistry() {
    if (this.initialized) {
      return this.getAllPages();
    }

    console.log('📋 PageRegistryService - Initializing page registry...');

    // Sektörel sayfalar
    await this.registerSectorPages();

    // Admin sayfaları
    await this.registerAdminPages();

    // Business yönetim sayfaları
    await this.registerBusinessPages();

    // Genel sistem sayfaları
    await this.registerSystemPages();

    this.initialized = true;
    console.log(`📋 PageRegistryService - Registered ${this.pages.size} pages in ${this.categories.size} categories`);

    return this.getAllPages();
  }

  /**
   * Sektörel sayfaları kaydet (Food-Beverage, Retail, Service)
   */
  async registerSectorPages() {
    // Food & Beverage Sektörü
    const foodBeveragePages = [
      {
        id: 'food-dashboard',
        title: 'Restoran Dashboard',
        path: '/sectors/restaurant/dashboard',
        category: 'dashboard',
        sector: 'food-beverage',
        description: 'Ana restoran yönetim dashboard\'u',
        icon: 'DashboardIcon',
        requiredModules: ['basic_access'],
        businessTypes: ['restaurant', 'cafe', 'fast_food'],
        userRoles: ['admin', 'manager'],
        isActive: true
      },
      {
        id: 'pos-dashboard',
        title: 'POS Dashboard',
        path: '/sectors/restaurant/pos/dashboard',
        category: 'pos',
        sector: 'food-beverage',
        description: 'Satış noktası yönetim ekranı',
        icon: 'POSIcon',
        requiredModules: ['pos_basic'],
        businessTypes: ['restaurant', 'cafe', 'fast_food'],
        userRoles: ['admin', 'manager', 'cashier'],
        isActive: true
      },
      {
        id: 'pos-tables',
        title: 'Masa Yönetimi',
        path: '/sectors/restaurant/pos/tables',
        category: 'pos',
        sector: 'food-beverage',
        description: 'Masa durumu ve rezervasyon',
        icon: 'TableIcon',
        requiredModules: ['pos_basic', 'table_service'],
        businessTypes: ['restaurant', 'cafe'],
        userRoles: ['admin', 'manager', 'waiter'],
        isActive: true
      },
      {
        id: 'table-service',
        title: 'Masa Servisi',
        path: '/sectors/restaurant/pos/table-service',
        category: 'pos',
        sector: 'food-beverage',
        description: 'Masa servis yönetimi',
        icon: 'TableIcon',
        requiredModules: ['pos_basic', 'table_service'],
        businessTypes: ['restaurant', 'cafe'],
        userRoles: ['admin', 'manager', 'waiter'],
        isActive: true
      },
      {
        id: 'quick-sales',
        title: 'Hızlı Satış',
        path: '/sectors/restaurant/pos/quick-sales',
        category: 'pos',
        sector: 'food-beverage',
        description: 'Tezgah satış ve hızlı işlemler',
        icon: 'OrderIcon',
        requiredModules: ['pos_basic', 'quick_sales'],
        businessTypes: ['restaurant', 'cafe', 'fast_food'],
        userRoles: ['admin', 'manager', 'cashier'],
        isActive: true
      },
      {
        id: 'phone-orders',
        title: 'Telefon Siparişleri',
        path: '/sectors/restaurant/pos/phone-orders',
        category: 'pos',
        sector: 'food-beverage',
        description: 'Telefon siparişi alma ve yönetimi',
        icon: 'PhoneIcon',
        requiredModules: ['pos_basic', 'phone_orders'],
        businessTypes: ['restaurant', 'fast_food'],
        userRoles: ['admin', 'manager', 'cashier'],
        isActive: true
      },
      {
        id: 'menu-management',
        title: 'Menü Yönetimi',
        path: '/sectors/food-beverage/pages/settings/MenuManagement',
        category: 'settings',
        sector: 'food-beverage',
        description: 'Ürün ve menü düzenleme',
        icon: 'MenuIcon',
        requiredModules: ['menu_management'],
        businessTypes: ['restaurant', 'cafe', 'fast_food'],
        userRoles: ['admin', 'manager'],
        isActive: true
      },
      {
        id: 'restaurant-settings',
        title: 'Restoran Ayarları',
        path: '/sectors/restaurant/settings',
        category: 'settings',
        sector: 'food-beverage',
        description: 'Genel restoran ayarları',
        icon: 'SettingsIcon',
        requiredModules: ['basic_access'],
        businessTypes: ['restaurant', 'cafe', 'fast_food'],
        userRoles: ['admin', 'manager'],
        isActive: true
      },
      // --- RAPORLAR & ANALİTİK ALT SAYFALARI ---
      {
        id: 'report-platform-orders',
        title: 'Platform Sipariş Geçmişi',
        path: '/sectors/food-beverage/reports/platform-orders',
        category: 'reports',
        sector: 'food-beverage',
        description: 'Trendyol, Yemeksepeti, Getir sipariş raporları',
        icon: 'ShoppingCartIcon',
        color: 'blue',
        badge: 'Gerçek Veri',
        requiredModules: ['reporting_basic'],
        isActive: true
      },
      {
        id: 'report-customer-reviews',
        title: 'Müşteri Yorumları',
        path: '/sectors/food-beverage/customers/reviews',
        category: 'reports',
        sector: 'food-beverage',
        description: 'Platform yorumlarını görüntüle ve yanıtla',
        icon: 'StarIcon',
        color: 'yellow',
        badge: 'Gerçek Veri',
        requiredModules: ['crm'],
        isActive: true
      },
      {
        id: 'report-finance-payouts',
        title: 'Finansal Raporlar',
        path: '/sectors/food-beverage/finance',
        category: 'reports',
        sector: 'food-beverage',
        description: 'Platform ödemeleri ve finansal durum',
        icon: 'CurrencyDollarIcon',
        color: 'green',
        badge: 'Gerçek Veri',
        requiredModules: ['accounting'],
        isActive: true
      },
      {
        id: 'report-sales-analytics',
        title: 'Satış Analizi',
        path: '/sectors/food-beverage/reports',
        category: 'reports',
        sector: 'food-beverage',
        description: 'Detaylı satış raporları ve trendler',
        icon: 'ChartBarIcon',
        color: 'indigo',
        requiredModules: ['reporting_basic'],
        isActive: true
      },

      {
        id: 'report-custom-dashboards',
        title: 'Özel Dashboardlar',
        path: '/sectors/food-beverage/analytics/custom-dashboards',
        category: 'reports',
        sector: 'food-beverage',
        description: 'Kişiselleştirilmiş analitik paneller',
        icon: 'PresentationChartLineIcon',
        color: 'purple',
        requiredModules: ['reporting_advanced'],
        isActive: true
      },
      {
        id: 'report-income-analysis',
        title: 'Gelir Analizi',
        path: '/sectors/food-beverage/finance/income-analysis',
        category: 'reports',
        sector: 'food-beverage',
        description: 'Günlük, haftalık, aylık gelir raporları',
        icon: 'DocumentChartBarIcon',
        color: 'emerald',
        requiredModules: ['accounting'],
        isActive: true
      },
      {
        id: 'report-platform-analysis',
        title: 'Platform Analizi',
        path: '/sectors/food-beverage/finance/platform-analysis',
        category: 'reports',
        sector: 'food-beverage',
        description: 'Platform bazlı gelir, gider ve komisyon denetimi',
        icon: 'DevicePhoneMobileIcon',
        color: 'indigo',
        requiredModules: ['accounting'],
        isActive: true
      },
      {
        id: 'report-product-profitability',
        title: 'Ürün Performansı',
        path: '/sectors/food-beverage/finance/detailed-product-profitability',
        category: 'reports',
        sector: 'food-beverage',
        description: 'En çok satan ürünler ve karlılık',
        icon: 'CubeIcon',
        color: 'orange',
        requiredModules: ['accounting'],
        isActive: true
      },
      {
        id: 'report-customer-crm',
        title: 'Müşteri Analizi',
        path: '/sectors/food-beverage/customers/customer-relationship-management',
        category: 'reports',
        sector: 'food-beverage',
        description: 'Müşteri davranışları ve segmentasyon',
        icon: 'UserGroupIcon',
        color: 'pink',
        requiredModules: ['crm'],
        isActive: true
      },
      {
        id: 'report-order-tracking',
        title: 'Sipariş Trendleri',
        path: '/sectors/food-beverage/reports',
        category: 'reports',
        sector: 'food-beverage',
        description: 'Sipariş yoğunluğu ve zaman analizi',
        icon: 'ClockIcon',
        color: 'cyan',
        requiredModules: ['reporting_basic'],
        isActive: true
      },

      {
        id: 'report-courier-perf',
        title: 'Teslimat Performansı',
        path: '/sectors/food-beverage/operations/courier-management',
        category: 'reports',
        sector: 'food-beverage',
        description: 'Kurye ve teslimat süre analizi',
        icon: 'TruckIcon',
        color: 'violet',
        requiredModules: ['pos_basic'],
        isActive: true
      },
      {
        id: 'report-web-traffic',
        title: 'Web Trafiği (Analytics)',
        path: '/sectors/food-beverage/reports/web-traffic',
        category: 'reports',
        sector: 'food-beverage',
        description: 'QR Menü ziyaretçi ve trafik analizleri',
        icon: 'PresentationChartLineIcon',
        color: 'blue',
        badge: 'Yeni Modül',
        requiredModules: ['qr_menu'],
        isActive: true
      },
      {
        id: 'report-order-heatmap',
        title: 'Sipariş Yoğunluk Haritası',
        path: '/sectors/food-beverage/reports/order-heatmap',
        category: 'reports',
        sector: 'food-beverage',
        description: 'Bölgesel sipariş dağılımı ve ısı haritası analizi',
        icon: 'MapPinIcon',
        color: 'orange',
        badge: 'Isı Haritası',
        requiredModules: ['reporting_advanced'],
        isActive: true
      },
      {
        id: 'qr-order-management',
        title: 'QR Sipariş Yönetimi',
        path: '/sectors/restaurant/pos/qr-order-management',
        category: 'pos',
        sector: 'food-beverage',
        description: 'QR kod üzerinden siparişler',
        icon: 'MenuIcon',
        requiredModules: ['qr_menu', 'pos_basic'],
        businessTypes: ['restaurant', 'cafe'],
        userRoles: ['admin', 'manager', 'waiter'],
        isActive: true
      },
      {
        id: 'whatsapp-integration',
        title: 'WhatsApp Entegrasyonu',
        path: '/business/whatsapp',
        category: 'integrations',
        sector: 'food-beverage',
        description: 'WhatsApp siparişi ve müşteri yönetimi',
        icon: 'IntegrationIcon',
        requiredModules: ['whatsapp_integration'],
        businessTypes: ['restaurant', 'cafe', 'fast_food'],
        userRoles: ['admin', 'manager'],
        isActive: true
      },
      {
        id: 'social-media-management',
        title: 'Sosyal Medya Yönetimi',
        path: '/social-media',
        category: 'marketing',
        sector: 'food-beverage',
        description: 'Sosyal medya paylaşım ve yönetimi',
        icon: 'IntegrationIcon',
        requiredModules: ['social_media_management'],
        businessTypes: ['restaurant', 'cafe', 'fast_food'],
        userRoles: ['admin', 'manager', 'marketing'],
        isActive: true
      },
      {
        id: 'social-media-new',
        title: 'Yeni Paylaşım',
        path: '/social-media/new',
        category: 'marketing',
        sector: 'food-beverage',
        description: 'Yeni sosyal medya içeriği oluştur',
        icon: 'SparklesIcon',
        requiredModules: ['social_media_management'],
        businessTypes: ['restaurant', 'cafe', 'fast_food'],
        userRoles: ['admin', 'manager', 'marketing'],
        isActive: true
      },
      {
        id: 'campaign-management',
        title: 'Kampanya Yönetimi',
        path: '/sectors/restaurant/campaigns/campaign-management',
        category: 'marketing',
        sector: 'food-beverage',
        description: 'Promosyon ve kampanya yönetimi',
        icon: 'NotificationIcon',
        requiredModules: ['campaign_management'],
        businessTypes: ['restaurant', 'cafe', 'fast_food'],
        userRoles: ['admin', 'manager', 'marketing'],
        isActive: true
      },
      // --- NEWLY DISCOVERED REAL PAGES ---
      {
        id: 'self-service-kiosk',
        title: 'Self-Service Kiosk',
        path: '/sectors/food-beverage/pos/kiosk', // Matching existing file location logic
        category: 'pos',
        sector: 'food-beverage',
        description: 'Müşteri sipariş kiosku',
        icon: 'ComputerDesktopIcon',
        requiredModules: ['pos_kiosk'],
        businessTypes: ['fast_food', 'restaurant'],
        userRoles: ['admin', 'manager'],
        isActive: true
      },
      {
        id: 'weight-sales',
        title: 'Tartılı Satış',
        path: '/sectors/food-beverage/pos/weight-sales',
        category: 'pos',
        sector: 'food-beverage',
        description: 'Terazi entegreli satış ekranı',
        icon: 'ScaleIcon',
        requiredModules: ['pos_basic', 'scale_integration'],
        businessTypes: ['restaurant', 'market'],
        userRoles: ['admin', 'manager', 'cashier'],
        isActive: true
      },
      {
        id: 'inventory-management-ops',
        title: 'Stok Yönetimi (Ops)',
        path: '/sectors/food-beverage/operations/inventory-management',
        category: 'inventory',
        sector: 'food-beverage',
        description: 'Operasyonel stok yönetimi',
        icon: 'CubeIcon',
        requiredModules: ['inventory_management'],
        businessTypes: ['restaurant', 'cafe'],
        userRoles: ['admin', 'manager', 'inventory'],
        isActive: true
      },
      {
        id: 'order-tracking',
        title: 'Sipariş Takibi',
        path: '/sectors/food-beverage/operations/order-tracking',
        category: 'orders',
        sector: 'food-beverage',
        description: 'Aktif sipariş takibi',
        icon: 'OrderIcon',
        requiredModules: ['order_management'],
        businessTypes: ['restaurant', 'cafe', 'fast_food'],
        userRoles: ['admin', 'manager', 'waiter', 'cashier'],
        isActive: true
      },
      {
        id: 'phone-order-management',
        title: 'Telefon Sipariş Yön.',
        path: '/sectors/food-beverage/operations/phone-order-management',
        category: 'orders',
        sector: 'food-beverage',
        description: 'Telefon siparişleri operasyon merkezi',
        icon: 'PhoneIcon',
        requiredModules: ['phone_orders'],
        businessTypes: ['restaurant', 'fast_food'],
        userRoles: ['admin', 'manager', 'cashier'],
        isActive: true
      },
      {
        id: 'qr-order-screen',
        title: 'QR Sipariş Ekranı',
        path: '/sectors/food-beverage/qr/qr-order-screen',
        category: 'pos',
        sector: 'food-beverage',
        description: 'Müşteri QR sipariş arayüzü',
        icon: 'DevicePhoneMobileIcon',
        requiredModules: ['qr_menu'],
        businessTypes: ['restaurant', 'cafe'],
        userRoles: ['admin', 'manager', 'waiter'],
        isActive: true
      },
      {
        id: 'scale-integration',
        title: 'Terazi Entegrasyonu',
        path: '/sectors/food-beverage/scales/scale-integration',
        category: 'settings',
        sector: 'food-beverage',
        description: 'Terazi bağlantı ayarları',
        icon: 'CogIcon',
        requiredModules: ['scale_integration'],
        businessTypes: ['restaurant', 'market'],
        userRoles: ['admin', 'manager'],
        isActive: true
      },
      {
        id: 'scale-calibration',
        title: 'Terazi Kalibrasyonu',
        path: '/sectors/food-beverage/scales/scale-calibration',
        category: 'settings',
        sector: 'food-beverage',
        description: 'Terazi kalibrasyon işlemleri',
        icon: 'WrenchIcon',
        requiredModules: ['scale_integration'],
        businessTypes: ['restaurant', 'market'],
        userRoles: ['admin', 'manager'],
        isActive: true
      }
    ];

    // Retail Sektörü
    const retailPages = [
      {
        id: 'retail-dashboard',
        title: 'Perakende Dashboard',
        path: '/sectors/retail/dashboard',
        category: 'dashboard',
        sector: 'retail',
        description: 'Ana perakende yönetim dashboard\'u',
        icon: 'DashboardIcon',
        requiredModules: ['basic_access'],
        businessTypes: ['retail', 'ecommerce'],
        userRoles: ['admin', 'manager'],
        isActive: true
      },
      {
        id: 'inventory-management',
        title: 'Stok Yönetimi',
        path: '/sectors/retail/inventory/products',
        category: 'inventory',
        sector: 'retail',
        description: 'Ürün ve stok takibi',
        icon: 'MenuIcon',
        requiredModules: ['inventory_management'],
        businessTypes: ['retail', 'ecommerce'],
        userRoles: ['admin', 'manager', 'inventory'],
        isActive: true
      }
    ];

    // Service Sektörü
    const servicePages = [
      {
        id: 'service-dashboard',
        title: 'Hizmet Dashboard',
        path: '/sectors/service/dashboard',
        category: 'dashboard',
        sector: 'service',
        description: 'Ana hizmet yönetim dashboard\'u',
        icon: 'DashboardIcon',
        requiredModules: ['basic_access'],
        businessTypes: ['beauty', 'health', 'consulting'],
        userRoles: ['admin', 'manager'],
        isActive: true
      },
      {
        id: 'appointment-management',
        title: 'Randevu Yönetimi',
        path: '/sectors/service/appointments/calendar',
        category: 'appointments',
        sector: 'service',
        description: 'Randevu takvimi ve yönetimi',
        icon: 'CustomerIcon',
        requiredModules: ['appointment_management'],
        businessTypes: ['beauty', 'health', 'consulting'],
        userRoles: ['admin', 'manager', 'staff'],
        isActive: true
      }
    ];

    // Sayfaları kaydet
    [...foodBeveragePages, ...retailPages, ...servicePages].forEach(page => {
      this.registerPage(page);
    });

    // Kategorileri kaydet
    this.registerCategory('dashboard', 'Dashboard\'lar', 'Ana yönetim ekranları');
    this.registerCategory('pos', 'POS & Satış', 'Satış noktası işlemleri');
    this.registerCategory('settings', 'Ayarlar', 'Sistem ayarları ve yapılandırma');
    this.registerCategory('integrations', 'Entegrasyonlar', 'Üçüncü parti entegrasyonlar');
    this.registerCategory('marketing', 'Pazarlama', 'Pazarlama ve kampanya araçları');
    this.registerCategory('inventory', 'Stok & Envanter', 'Stok yönetimi ve envanter');
    this.registerCategory('appointments', 'Randevular', 'Randevu yönetimi');
  }

  /**
   * Admin sayfalarını kaydet
   */
  async registerAdminPages() {
    const adminPages = [
      {
        id: 'admin-dashboard',
        title: 'Admin Dashboard',
        path: '/dashboard/admin/dashboard',
        category: 'admin',
        sector: 'system',
        description: 'Sistem yöneticisi ana ekranı',
        icon: 'DashboardIcon',
        requiredModules: ['admin_access'],
        businessTypes: ['all'],
        userRoles: ['system_admin'],
        isActive: true
      },
      {
        id: 'admin-sidebar-manager',
        title: 'Sidebar Yönetimi',
        path: '/dashboard/admin/sidebar-manager',
        category: 'admin',
        sector: 'system',
        description: 'Menü ve sidebar yönetimi',
        icon: 'SettingsIcon',
        requiredModules: ['admin_access'],
        businessTypes: ['all'],
        userRoles: ['system_admin'],
        isActive: true
      },
      {
        id: 'admin-subscription',
        title: 'Abonelik Yönetimi',
        path: '/dashboard/admin/subscription',
        category: 'admin',
        sector: 'system',
        description: 'Modül ve abonelik yönetimi',
        icon: 'PaymentIcon',
        requiredModules: ['admin_access'],
        businessTypes: ['all'],
        userRoles: ['system_admin'],
        isActive: true
      },
      {
        id: 'admin-user-management',
        title: 'Kullanıcı Yönetimi',
        path: '/dashboard/admin/users',
        category: 'admin',
        sector: 'system',
        description: 'Sistem kullanıcıları ve roller',
        icon: 'UserIcon',
        requiredModules: ['admin_access'],
        businessTypes: ['all'],
        userRoles: ['system_admin'],
        isActive: true
      }
    ];

    adminPages.forEach(page => {
      this.registerPage(page);
    });

    this.registerCategory('admin', 'Sistem Yönetimi', 'Admin ve sistem yönetimi');
  }

  /**
   * Business yönetim sayfalarını kaydet
   */
  async registerBusinessPages() {
    const businessPages = [
      {
        id: 'business-dashboard',
        title: 'İşletme Dashboard',
        path: '/dashboard',
        category: 'business',
        sector: 'business',
        description: 'Ana işletme yönetim ekranı',
        icon: 'DashboardIcon',
        requiredModules: ['basic_access'],
        businessTypes: ['all'],
        userRoles: ['business_owner', 'manager'],
        isActive: true
      },
      {
        id: 'brand-catalog',
        title: 'İşletme Katalogu',
        path: '/brands',
        category: 'business',
        sector: 'business',
        description: 'İşletme ve marka yönetimi',
        icon: 'MenuIcon',
        requiredModules: ['basic_access'],
        businessTypes: ['all'],
        userRoles: ['business_owner', 'manager'],
        isActive: true
      },
      {
        id: 'subscription-management',
        title: 'Abonelik Yönetimi',
        path: '/subscription/unified',
        category: 'business',
        sector: 'business',
        description: 'Abonelik paketleri ve ödemeler',
        icon: 'PaymentIcon',
        requiredModules: ['basic_access'],
        businessTypes: ['all'],
        userRoles: ['business_owner'],
        isActive: true
      }
    ];

    businessPages.forEach(page => {
      this.registerPage(page);
    });

    this.registerCategory('business', 'İşletme Yönetimi', 'Genel işletme yönetimi');
  }

  /**
   * Sistem sayfalarını kaydet
   */
  async registerSystemPages() {
    const systemPages = [
      {
        id: 'login',
        title: 'Giriş Yap',
        path: '/login',
        category: 'auth',
        sector: 'system',
        description: 'Kullanıcı giriş sayfası',
        icon: 'UserIcon',
        requiredModules: [],
        businessTypes: ['all'],
        userRoles: ['all'],
        isActive: true
      },
      {
        id: 'register',
        title: 'Kayıt Ol',
        path: '/register',
        category: 'auth',
        sector: 'system',
        description: 'Yeni kullanıcı kaydı',
        icon: 'UserIcon',
        requiredModules: [],
        businessTypes: ['all'],
        userRoles: ['all'],
        isActive: true
      }
    ];

    systemPages.forEach(page => {
      this.registerPage(page);
    });

    this.registerCategory('auth', 'Kimlik Doğrulama', 'Giriş ve kayıt sayfaları');
  }

  /**
   * Bir sayfayı kayıt sistemine ekle
   */
  registerPage(pageData) {
    const page = {
      ...pageData,
      registeredAt: new Date(),
      searchTerms: this.generateSearchTerms(pageData)
    };

    this.pages.set(pageData.id, page);
    console.log(`📋 Registered page: ${page.title} (${page.path})`);
  }

  /**
   * Bir kategoriyi kayıt sistemine ekle
   */
  registerCategory(id, name, description) {
    this.categories.set(id, {
      id,
      name,
      description,
      registeredAt: new Date()
    });
  }

  /**
   * Sayfa için arama terimlerini oluştur
   */
  generateSearchTerms(pageData) {
    const terms = [
      pageData.title.toLowerCase(),
      pageData.path.toLowerCase(),
      pageData.description.toLowerCase(),
      pageData.category.toLowerCase(),
      pageData.sector.toLowerCase(),
      ...(pageData.requiredModules || []).map(m => m.toLowerCase()),
      ...(pageData.businessTypes || []).map(t => t.toLowerCase()),
      ...(pageData.userRoles || []).map(r => r.toLowerCase())
    ];

    return [...new Set(terms)]; // Unique terms
  }

  /**
   * Tüm kayıtlı sayfaları döndür
   */
  getAllPages() {
    return Array.from(this.pages.values());
  }

  /**
   * Kategoriye göre sayfaları döndür
   */
  getPagesByCategory(categoryId) {
    return this.getAllPages().filter(page => page.category === categoryId);
  }

  /**
   * Sektöre göre sayfaları döndür
   */
  getPagesBySector(sectorId) {
    return this.getAllPages().filter(page => page.sector === sectorId);
  }

  /**
   * Modül gereksinimlerine göre sayfaları döndür
   */
  getPagesByModule(moduleIds = []) {
    if (!moduleIds.length) return this.getAllPages();

    return this.getAllPages().filter(page => {
      if (!page.requiredModules || page.requiredModules.length === 0) {
        return true; // Modül gerektirmeyenler her zaman görünür
      }

      // En az bir gerekli modül var mı?
      return page.requiredModules.some(module => moduleIds.includes(module));
    });
  }

  /**
   * Sayfa arama işlemi
   */
  searchPages(query, filters = {}) {
    if (!this.initialized) {
      console.warn('PageRegistryService not initialized. Call initializePageRegistry() first.');
      return [];
    }

    let pages = this.getAllPages();

    // Text search
    if (query && query.trim()) {
      const searchQuery = query.toLowerCase().trim();
      pages = pages.filter(page =>
        page.searchTerms.some(term => term.includes(searchQuery))
      );
    }

    // Apply filters
    if (filters.category) {
      pages = pages.filter(page => page.category === filters.category);
    }

    if (filters.sector) {
      pages = pages.filter(page => page.sector === filters.sector);
    }

    if (filters.businessTypes && filters.businessTypes.length) {
      pages = pages.filter(page =>
        page.businessTypes.some(type =>
          filters.businessTypes.includes(type) || page.businessTypes.includes('all')
        )
      );
    }

    if (filters.userRoles && filters.userRoles.length) {
      pages = pages.filter(page =>
        page.userRoles.some(role =>
          filters.userRoles.includes(role) || page.userRoles.includes('all')
        )
      );
    }

    if (filters.modules && filters.modules.length) {
      pages = this.getPagesByModule(filters.modules).filter(page => pages.includes(page));
    }

    // Sort by relevance
    return this.sortByRelevance(pages, query);
  }

  /**
   * Arama sonuçlarını relevansa göre sırala
   */
  sortByRelevance(pages, query) {
    if (!query) return pages;

    const searchQuery = query.toLowerCase().trim();

    return pages.sort((a, b) => {
      let scoreA = 0;
      let scoreB = 0;

      // Title exact match gets highest score
      if (a.title.toLowerCase().includes(searchQuery)) scoreA += 100;
      if (b.title.toLowerCase().includes(searchQuery)) scoreB += 100;

      // Path match gets high score
      if (a.path.toLowerCase().includes(searchQuery)) scoreA += 50;
      if (b.path.toLowerCase().includes(searchQuery)) scoreB += 50;

      // Description match gets medium score
      if (a.description.toLowerCase().includes(searchQuery)) scoreA += 25;
      if (b.description.toLowerCase().includes(searchQuery)) scoreB += 25;

      // Module/category match gets low score
      if (a.searchTerms.some(term => term.includes(searchQuery))) scoreA += 10;
      if (b.searchTerms.some(term => term.includes(searchQuery))) scoreB += 10;

      return scoreB - scoreA;
    });
  }

  /**
   * Tüm kategorileri döndür
   */
  getAllCategories() {
    return Array.from(this.categories.values());
  }

  /**
   * Route validation - sayfa gerçekten mevcut mu?
   */
  async validateRoute(path) {
    try {
      // Bu basit bir kontrol - production'da daha gelişmiş olabilir
      const page = this.getAllPages().find(p => p.path === path);

      return {
        isValid: !!page,
        page: page || null,
        suggestions: page ? [] : this.findSimilarRoutes(path)
      };
    } catch (error) {
      console.error('Route validation error:', error);
      return {
        isValid: false,
        page: null,
        suggestions: []
      };
    }
  }

  /**
   * Benzer route'ları bul (broken link için)
   */
  findSimilarRoutes(path) {
    const allPaths = this.getAllPages().map(p => p.path);

    return allPaths
      .filter(p => {
        // Simple similarity check
        const similarity = this.calculateSimilarity(path, p);
        return similarity > 0.6;
      })
      .slice(0, 5); // En fazla 5 öneri
  }

  /**
   * İki string arasındaki benzerliği hesapla
   */
  calculateSimilarity(str1, str2) {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;

    if (longer.length === 0) {
      return 1.0;
    }

    const editDistance = this.levenshteinDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  }

  /**
   * Levenshtein distance hesaplama
   */
  levenshteinDistance(str1, str2) {
    const matrix = [];

    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }

    return matrix[str2.length][str1.length];
  }

  /**
   * Firebase modules collection'dan mevcut modülleri yükle
   * @returns {Promise<Array>} Available modules list
   */
  async loadAvailableModules() {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5005'}/api/admin/modules`);

      if (!response.ok) {
        throw new Error(`Failed to load modules: ${response.status}`);
      }

      const data = await response.json();

      // Firebase'den gelen modül formatını normalize et
      const modules = data.modules || data || [];

      return modules.map(module => ({
        id: module.id || module.module_key,
        name: module.name || module.module_name,
        key: module.module_key || module.id,
        description: module.description || '',
        category: module.category || 'general',
        isActive: module.is_active !== false,
        permissions: module.permissions || [],
        businessTypes: module.business_types || ['all'],
        pages: module.pages || []
      }));

    } catch (error) {
      console.error('❌ PageRegistryService - Failed to load modules:', error);

      // Fallback: temel modüller
      return [
        {
          id: 'basic_access',
          name: 'Temel Erişim',
          key: 'basic_access',
          description: 'Temel sistem erişimi',
          category: 'core',
          isActive: true,
          permissions: ['read'],
          businessTypes: ['all']
        },
        {
          id: 'pos_basic',
          name: 'POS Temel',
          key: 'pos_basic',
          description: 'POS sistemi temel fonksiyonları',
          category: 'pos',
          isActive: true,
          permissions: ['read', 'write'],
          businessTypes: ['restaurant', 'retail']
        },
        {
          id: 'menu_management',
          name: 'Menü Yönetimi',
          key: 'menu_management',
          description: 'Menü ve ürün yönetimi',
          category: 'management',
          isActive: true,
          permissions: ['read', 'write', 'delete'],
          businessTypes: ['restaurant', 'cafe', 'fast_food']
        },
        {
          id: 'user_management',
          name: 'Kullanıcı Yönetimi',
          key: 'user_management',
          description: 'Kullanıcı ve rol yönetimi',
          category: 'admin',
          isActive: true,
          permissions: ['read', 'write', 'delete'],
          businessTypes: ['all']
        },
        {
          id: 'inventory_management',
          name: 'Envanter Yönetimi',
          key: 'inventory_management',
          description: 'Stok ve envanter takibi',
          category: 'management',
          isActive: true,
          permissions: ['read', 'write'],
          businessTypes: ['restaurant', 'retail']
        }
      ];
    }
  }

  /**
   * Modül ID'sine göre modül bilgilerini getir
   * @param {string} moduleId Module ID
   * @returns {Promise<Object|null>} Module data or null
   */
  async getModuleById(moduleId) {
    const modules = await this.loadAvailableModules();
    return modules.find(module => module.id === moduleId || module.key === moduleId) || null;
  }

  /**
   * Kategoriye göre modülleri getir
   * @param {string} category Module category
   * @returns {Promise<Array>} Modules in category
   */
  async getModulesByCategory(category) {
    const modules = await this.loadAvailableModules();
    return modules.filter(module => module.category === category);
  }

  /**
   * İş türüne göre uygun modülleri getir
   * @param {string} businessType Business type
   * @returns {Promise<Array>} Compatible modules
   */
  async getModulesForBusinessType(businessType) {
    const modules = await this.loadAvailableModules();
    return modules.filter(module =>
      module.businessTypes.includes('all') ||
      module.businessTypes.includes(businessType)
    );
  }
}

// Singleton instance
const pageRegistryService = new PageRegistryService();

export default pageRegistryService;